import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { LOGO_URL, NAV_MENU, PROGRAM_ROUTES } from "@/data";
import { api } from "@/lib/api";
import { useBooking } from "@/context/Booking";

const STATIC = NAV_MENU.map((m) => ({
  ...m,
  items: m.items.map((l) => ({ label: l, to: PROGRAM_ROUTES[l] || "" })),
}));

const tid = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const Navbar = () => {
  const { openBooking } = useBooking();
  const [menu, setMenu] = useState(STATIC);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const closeTimer = useRef(null);

  const openMenu = (i) => { clearTimeout(closeTimer.current); setActive(i); };
  const scheduleClose = () => { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setActive(null), 140); };

  useEffect(() => {
    api.get("/menu").then((r) => { if (Array.isArray(r.data) && r.data.length) setMenu(r.data); }).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const MAIN = menu.filter((m) => !m.cta);
  const BOOK = menu.find((m) => m.cta);
  const activeMenu = active !== null ? menu[active] : null;

  const SubLink = ({ it, top, onClick }) => {
    const cls = "group flex items-center gap-2.5 rounded-lg px-3 py-2.5 font-body text-[15px] text-brand-ink/75 transition-colors duration-200 hover:bg-white hover:text-brand-ink";
    const inner = (
      <>
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-line transition-colors duration-200 group-hover:bg-brand-magenta" />
        {it.label}
        {it.to && <ArrowUpRight className="ml-auto h-3.5 w-3.5 text-brand-magenta opacity-0 transition-opacity duration-200 group-hover:opacity-100" />}
      </>
    );
    return it.to ? (
      <Link to={it.to} data-testid={`nav-sub-${tid(it.label)}`} onClick={onClick} className={cls}>{inner}</Link>
    ) : (
      <a href={top.href} data-testid={`nav-sub-${tid(it.label)}`} onClick={onClick} className={cls}>{inner}</a>
    );
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-navbar"
      onMouseLeave={scheduleClose}
      onMouseEnter={() => clearTimeout(closeTimer.current)}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled || active !== null ? "bg-brand-cream/90 backdrop-blur-xl border-b border-brand-line py-3" : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <nav className="mx-auto flex max-w-[1500px] items-center justify-between gap-4 px-5 sm:px-8">
        <Link to="/" data-testid="nav-logo" className="flex shrink-0 items-center">
          <img src={LOGO_URL} alt="Jadoo Ki Jhappy" className="h-10 w-auto sm:h-11" />
        </Link>

        <div className="hidden items-center gap-x-6 xl:flex">
          {MAIN.map((m) => {
            const i = menu.indexOf(m);
            return (
              <div key={m.label} onMouseEnter={() => openMenu(i)}>
                <a href={m.href} data-testid={`nav-main-${i}`} className={`group flex items-center gap-1 font-heading text-[12.5px] font-semibold uppercase tracking-[0.1em] transition-colors duration-300 ${active === i ? "text-brand-magenta" : "text-brand-ink/80 hover:text-brand-ink"}`}>
                  {m.label}
                  {m.items?.length > 0 && <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${active === i ? "rotate-180" : ""}`} />}
                </a>
              </div>
            );
          })}
        </div>

        {BOOK && (
          <div className="hidden shrink-0 xl:block" onMouseEnter={() => openMenu(menu.indexOf(BOOK))}>
            <button type="button" onClick={() => { setActive(null); openBooking(); }} data-testid="nav-cta-book" className="group inline-flex items-center gap-2 rounded-full bg-brand-ink px-5 py-3 font-heading text-[12px] font-semibold uppercase tracking-[0.1em] text-white transition-transform duration-300 hover:scale-[1.04]">
              {BOOK.shortLabel || BOOK.label}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>
        )}

        <button data-testid="nav-mobile-toggle" onClick={() => setOpen((v) => !v)} className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-line bg-brand-cream/70 xl:hidden" aria-label="Toggle menu">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {activeMenu && activeMenu.items?.length > 0 && (
          <motion.div key="mega" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-x-0 top-full hidden xl:block" data-testid="nav-mega-panel">
            <div className="mx-auto max-w-[1500px] px-8">
              <div className="mt-2 overflow-hidden rounded-2xl border border-brand-line bg-brand-cream/95 shadow-2xl shadow-brand-ink/10 backdrop-blur-xl">
                <div className="grid grid-cols-[0.9fr_2.1fr]">
                  <div className="border-r border-brand-line bg-white/50 p-9">
                    <p className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-brand-magenta">{activeMenu.label}</p>
                    <p className="mt-4 font-heading text-2xl font-semibold leading-snug text-brand-ink">{activeMenu.tag}</p>
                    <a href={activeMenu.href} className="group mt-6 inline-flex items-center gap-1.5 font-heading text-sm font-semibold text-brand-ink transition-colors duration-300 hover:text-brand-magenta">
                      View section
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  </div>
                  <div className="grid grid-cols-3 gap-x-6 gap-y-1 p-9">
                    {activeMenu.items.map((it) => <SubLink key={it.label} it={it} top={activeMenu} onClick={() => setActive(null)} />)}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden border-t border-brand-line bg-brand-cream xl:hidden" data-testid="nav-mobile-menu">
            <div className="mx-auto max-h-[75vh] max-w-[1500px] overflow-y-auto px-5 pb-6 pt-2 sm:px-8">
              {menu.map((m, i) => (
                <div key={m.label} className="border-b border-brand-line">
                  {m.items?.length > 0 ? (
                    <button onClick={() => setExpanded(expanded === i ? null : i)} data-testid={`nav-mobile-main-${i}`} className="flex w-full items-center justify-between py-4 text-left font-heading text-lg font-semibold uppercase tracking-wide text-brand-ink">
                      {m.label}
                      <Plus className={`h-5 w-5 shrink-0 text-brand-magenta transition-transform duration-300 ${expanded === i ? "rotate-45" : ""}`} />
                    </button>
                  ) : (
                    <a href={m.href} onClick={() => setOpen(false)} data-testid={`nav-mobile-main-${i}`} className="flex w-full items-center justify-between py-4 text-left font-heading text-lg font-semibold uppercase tracking-wide text-brand-ink">
                      {m.label}
                    </a>
                  )}
                  <AnimatePresence>
                    {expanded === i && m.items?.length > 0 && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden">
                        <div className="flex flex-col gap-1 pb-4">
                          {m.items.map((it) => <SubLink key={it.label} it={it} top={m} onClick={() => setOpen(false)} />)}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
              {BOOK && (
                <button type="button" onClick={() => { setOpen(false); openBooking(); }} data-testid="nav-mobile-cta" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-magenta px-6 py-4 font-heading text-sm font-semibold uppercase tracking-wide text-white">
                  {BOOK.shortLabel || BOOK.label} <ArrowUpRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
